(function () {
  'use strict';

  // All Storefront traffic goes through the shared client (queue + unified
  // 1h cache + order-level guardrail). Loaded by theme.liquid before any
  // section script; if it's somehow absent, cards just keep their base
  // Algolia prices.
  const SF = window.StorefrontClient;
  if (!SF) return;

  // ---------------------------------------------------------------------------
  // PLP-scoped persistence (localStorage, 1h TTL)
  // ---------------------------------------------------------------------------
  // The unified StorefrontClient cache deliberately OMITS the OOS-sibling
  // substitutions and availability flips this checker resolves: publishing a
  // sibling's dollar amounts under the representative variant id would corrupt
  // the PDP / card-section reads that share that cache. But those resolutions
  // are the EXPENSIVE ones — each re-runs a throttle-gated multi-round
  // Storefront pipeline (availability query → sibling lookup → sub-probe). Left
  // uncached they re-resolve on EVERY page load, so a reload re-pays the whole
  // latency and the cards visibly "load from scratch". Persist just the PLP
  // view (variant → substituted/availability info, handle → range discount) in
  // its own key so a reload paints instantly and the pipeline is skipped for
  // products already resolved. TTL matches the unified cache. The PDP never
  // reads these keys, so its dollar amounts stay correct.
  // Sale-collection gate: on /collections/sale we ALSO let a product's
  // representative variant (the one Algolia displays, which drives the "Sale"
  // badge) contribute a discount to the multi-variant range — see
  // processMultiVariantDiscounts. Off this page only the price extremes count.
  function isSaleCollectionPage() {
    try {
      const el = document.getElementById('plp-collection-data');
      if (el) {
        const d = JSON.parse(el.textContent || '{}');
        if (d && typeof d.collectionHandle === 'string') return d.collectionHandle === 'sale';
      }
    } catch {}
    return /\/collections\/sale(?:[\/?#]|$)/.test(window.location.pathname || '');
  }
  const SALE_PAGE = isSaleCollectionPage();

  const PLP_CACHE_KEY = 'algolia_plp_avail_v2';
  // v3: range ends are now resolved with getDiscountsIsolated (peel the winner),
  // so a previously-suppressed end now shows its real discount. v2 entries cached
  // under the old batched probe could carry a false `hasRangeDiscount:false`;
  // bumped so they're dropped on deploy instead of lingering for the TTL.
  // (v2 had already moved from a single representative ratio to per-end probing.)
  // Sale-page ranges fold in the representative variant, so they live in their
  // OWN namespace — otherwise a `hasRangeDiscount:false` cached from a non-sale
  // PLP visit would seed checkedHandles and suppress the sale-page recompute.
  const PLP_RANGE_KEY = SALE_PAGE ? 'algolia_plp_ranges_sale_v1' : 'algolia_plp_ranges_v3';
  // Variant lists are page-independent (same product → same variants), so this
  // key is NOT namespaced by sale: a lookup done on any surface serves them all.
  const PLP_VARIANTS_KEY = 'algolia_plp_variants_v1';
  const PLP_TTL_MS = 60 * 60 * 1000;
  const PLP_MAX_ENTRIES = 800;

  function plpLoad(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return {};
      const all = JSON.parse(raw);
      const now = Date.now();
      const fresh = {};
      for (const k in all) {
        const e = all[k];
        if (e && e.ts && now - e.ts < PLP_TTL_MS && e.v) fresh[k] = e;
      }
      return fresh;
    } catch { return {}; }
  }
  const plpPersist = plpLoad(PLP_CACHE_KEY);            // variantId -> { v, ts }
  const plpRangePersist = plpLoad(PLP_RANGE_KEY);       // handle -> { v, ts }
  const plpVariantsPersist = plpLoad(PLP_VARIANTS_KEY); // productId -> { v, ts }
  let plpFlushTimer = null;
  function plpPrune(store) {
    const ids = Object.keys(store);
    if (ids.length <= PLP_MAX_ENTRIES) return;
    ids.sort((a, b) => store[a].ts - store[b].ts);
    for (const drop of ids.slice(0, ids.length - PLP_MAX_ENTRIES)) delete store[drop];
  }
  function plpFlush() {
    plpFlushTimer = null;
    try {
      plpPrune(plpPersist);
      plpPrune(plpRangePersist);
      plpPrune(plpVariantsPersist);
      localStorage.setItem(PLP_CACHE_KEY, JSON.stringify(plpPersist));
      localStorage.setItem(PLP_RANGE_KEY, JSON.stringify(plpRangePersist));
      localStorage.setItem(PLP_VARIANTS_KEY, JSON.stringify(plpVariantsPersist));
    } catch {}
  }
  function plpScheduleFlush() { if (!plpFlushTimer) plpFlushTimer = setTimeout(plpFlush, 250); }
  function plpSet(variantId, info) { plpPersist[String(variantId)] = { v: info, ts: Date.now() }; plpScheduleFlush(); }
  function plpSetRange(handle, info) { plpRangePersist[String(handle)] = { v: info, ts: Date.now() }; plpScheduleFlush(); }
  function plpSetVariants(productId, variants) { plpVariantsPersist[String(productId)] = { v: variants, ts: Date.now() }; plpScheduleFlush(); }

  // Seeded from the persisted PLP view so the pipeline skips products it already
  // resolved on a previous load (capped by the 1h TTL, same as the unified cache).
  const checkedVariants = new Set(Object.keys(plpPersist));

  // Global discount store — read by preact ProductCard components.
  // Pre-populate from the shared client cache (localStorage, 1h TTL) so
  // returning visitors see discounts on first render, then overlay the
  // PLP-scoped substitutions/availability the unified cache omits.
  if (!window.__algoliaDiscounts) {
    window.__algoliaDiscounts = new Map();
    const cached = SF.cacheAll();
    for (const [id, info] of Object.entries(cached)) {
      window.__algoliaDiscounts.set(String(id), info);
    }
    for (const [id, entry] of Object.entries(plpPersist)) {
      window.__algoliaDiscounts.set(String(id), entry.v);
    }
  }
  // Restore persisted range discounts so multi-variant cards paint their
  // discounted range on the first render too.
  if (!window.__algoliaRangeDiscounts) {
    window.__algoliaRangeDiscounts = new Map();
    for (const [handle, entry] of Object.entries(plpRangePersist)) {
      window.__algoliaRangeDiscounts.set(String(handle), entry.v);
    }
  }

  // Signals that the first discount check is complete — ProductCard shows
  // a price skeleton until this is true so prices don't flash/shift.
  // If the shared cache had data, mark ready immediately.
  if (typeof window.__discountsReady === 'undefined') {
    window.__discountsReady = window.__algoliaDiscounts.size > 0;
  }

  // ---------------------------------------------------------------------------
  // Variant ID extraction
  // ---------------------------------------------------------------------------

  // variantId → { productId, handle } from the Algolia hit, used to resolve a
  // SIBLING variant when the representative one is out of stock (see below).
  const hitMeta = new Map();

  function extractVariantIdsFromHits() {
    try {
      const hits = window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.hits;
      if (!hits?.length) return null;
      const ids = [];
      for (const hit of hits) {
        const raw = hit.variant_id || hit.variantId || hit.variants?.[0]?.id || hit.objectID;
        if (raw != null) {
          const id = String(raw);
          ids.push(id);
          if (hit.id) hitMeta.set(id, { productId: String(hit.id), handle: hit.handle, oos: hit.inventory_available === false });
        }
      }
      return ids.length > 0 ? ids : null;
    } catch {
      return null;
    }
  }

  function extractVariantIdsFromDOM() {
    const btns = document.querySelectorAll('.product-add-cart[data-variant-id]');
    if (btns.length === 0) return null;
    const ids = [];
    for (const btn of btns) {
      const id = btn.getAttribute('data-variant-id');
      if (id) ids.push(id);
    }
    return ids.length > 0 ? ids : null;
  }

  function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  // ---------------------------------------------------------------------------
  // Sibling variant resolution (representative variant out of stock)
  // ---------------------------------------------------------------------------

  // Algolia's representative variant for a product can be out of stock even
  // when other variants are purchasable (the PDP then defaults to an in-stock
  // one and DOES show the discount). A qty-0 cart line never gets allocations
  // (the probe marks it `unresolved`), so we look up the product's variants
  // and re-check the first in-stock sibling. The SAME variant list also feeds
  // the multi-variant range pass (it needs every variant's price to find the
  // cheapest and most expensive ends), so the query carries `price` too and the
  // result is memoized per product — both consumers share one fetch.
  const PRODUCT_VARIANTS_QUERY = `
    query ProductVariants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          variants(first: 50) {
            nodes { id availableForSale price { amount } }
          }
        }
      }
    }
  `;
  // A full 48-product grid resolves in ONE request: measured requestedQueryCost
  // is 16 (nodes queries don't multiply by variants(first:N) the way cartCreate
  // bills lines), and this query is not subject to the cartCreate anti-bot.
  const VARIANTS_PER_QUERY = 48;

  function extractVariantNumericId(gid) {
    const match = gid.match(/ProductVariant\/(\d+)/);
    return match ? match[1] : null;
  }

  function extractProductNumericId(gid) {
    const match = String(gid || '').match(/Product\/(\d+)/);
    return match ? match[1] : null;
  }

  // Memo of productId → [{ id, available, price }]. Both the OOS-sibling
  // resolution and the multi-variant range pass read this. Only DEFINITIVE
  // results land here; a failed/throttled lookup is left out so it retries.
  // Seeded from localStorage (1h TTL, page-independent) so a product looked up
  // on any surface — collection, search — never re-fetches on the next one.
  const productVariantsCache = new Map();
  for (const [pid, entry] of Object.entries(plpVariantsPersist)) {
    productVariantsCache.set(String(pid), entry.v);
  }

  // Resolve the full variant list (id, availability, price) for a set of
  // product ids. Returns Map<productId(str), variants[]> covering only the
  // products that resolved — a missing entry means the lookup failed/throttled
  // and the caller should retry on the next render.
  async function fetchProductVariants(productIds) {
    const out = new Map();
    const toQuery = [];
    for (const pid of productIds) {
      const key = String(pid);
      if (!key) continue;
      if (productVariantsCache.has(key)) { out.set(key, productVariantsCache.get(key)); continue; }
      if (!toQuery.includes(key)) toQuery.push(key);
    }
    await Promise.all(chunk(toQuery, VARIANTS_PER_QUERY).map(async (ids) => {
      const gids = ids.map(id => `gid://shopify/Product/${id}`);
      const json = await SF.request(PRODUCT_VARIANTS_QUERY, { ids: gids });
      const nodes = json?.data?.nodes;
      if (!nodes) return; // failed/throttled — those products retry next render
      for (const node of nodes) {
        if (!node?.id) continue;
        const pid = extractProductNumericId(node.id);
        if (!pid) continue;
        const variants = (node.variants?.nodes || [])
          .map(v => ({
            id: extractVariantNumericId(v.id),
            available: !!v.availableForSale,
            price: parseFloat(v.price?.amount || 0),
          }))
          .filter(v => v.id);
        productVariantsCache.set(pid, variants);
        plpSetVariants(pid, variants);
        out.set(pid, variants);
      }
    }));
    return out;
  }

  // Session memo of representative → substitute resolutions. Only DEFINITIVE
  // results land here (a substitute id, or null = nothing purchasable);
  // failed/throttled lookups are left out so they retry.
  const substituteCache = new Map();

  // Map<repVariantId, substituteVariantId | null>. null = product has NO
  // purchasable variant (truly sold out → settle as no-discount). A repId with
  // NO entry means the lookup failed — caller leaves it unchecked for retry.
  async function resolveSubstitutes(repIds) {
    const out = new Map();
    const repByProduct = new Map();
    const productIds = [];
    for (const repId of repIds) {
      const key = String(repId);
      if (substituteCache.has(key)) { out.set(repId, substituteCache.get(key)); continue; }
      const meta = hitMeta.get(key);
      if (!meta) { out.set(repId, null); continue; } // transient — don't memoize
      repByProduct.set(String(meta.productId), repId);
      productIds.push(String(meta.productId));
    }
    const variantsByProduct = await fetchProductVariants(productIds);
    for (const [pid, repId] of repByProduct) {
      if (!variantsByProduct.has(pid)) continue; // failed/throttled — retry next render
      const variants = variantsByProduct.get(pid);
      const sub = variants.find(v => v.available);
      const resolved = sub ? sub.id : null;
      out.set(repId, resolved);
      substituteCache.set(String(repId), resolved); // definitive — memoize
    }
    return out;
  }

  // ---------------------------------------------------------------------------
  // Publish discount data (consumed by preact ProductCard via state)
  // ---------------------------------------------------------------------------

  function publishDiscount(variantId, discountInfo) {
    // Availability never regresses: a later discount settle (which knows
    // nothing about stock) must not erase a productAvailable flag published
    // earlier by the availability pre-pass or the sibling phase — cards that
    // re-mount on filter changes read this map cold.
    const prev = window.__algoliaDiscounts.get(String(variantId));
    if (prev && prev.productAvailable && !discountInfo.productAvailable) {
      discountInfo = Object.assign({}, discountInfo, { productAvailable: true });
    }
    window.__algoliaDiscounts.set(String(variantId), discountInfo);
    // Persist only the PLP-extra (OOS-sibling substitutions / availability
    // flips). Plain discounts already live in the unified StorefrontClient
    // cache; this captures exactly the expensive-to-resolve data the unified
    // cache omits, so it isn't recomputed on the next load.
    if (discountInfo && (discountInfo.substituted || discountInfo.productAvailable)) {
      plpSet(variantId, discountInfo);
    }
    document.dispatchEvent(new CustomEvent('algolia.discount.resolved', {
      detail: { variantId: String(variantId), discountInfo },
    }));
  }

  function settle(variantId, info) {
    checkedVariants.add(variantId);
    SF.cacheSet(variantId, info);
    publishDiscount(variantId, info);
  }

  // ---------------------------------------------------------------------------
  // Multi-variant range discount
  // ---------------------------------------------------------------------------

  const checkedHandles = new Set(Object.keys(plpRangePersist));

  function extractMultiVariantHits() {
    try {
      const hits = window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.hits;
      if (!hits?.length) return [];
      const seen = new Set();
      const results = [];
      for (const hit of hits) {
        const handle = hit.handle;
        const count = hit.variants_count || 0;
        if (handle && count > 1 && !seen.has(handle) && !checkedHandles.has(handle)) {
          seen.add(handle);
          results.push({
            handle,
            // Product id drives the per-product variant lookup that resolves
            // each end of the range independently.
            productId: String(hit.id || ''),
            // Representative variant Algolia displays for this product; on the
            // sale page its discount can pull the range's sale floor down.
            repVariantId: String(hit.variant_id || hit.variantId || hit.variants?.[0]?.id || hit.objectID || ''),
            // Aggregate range from Algolia, kept as a fallback only.
            originalMin: parseFloat(hit.variants_min_price),
            originalMax: parseFloat(hit.variants_max_price),
          });
        }
      }
      return results;
    } catch { return []; }
  }

  function publishRangeDiscount(info) {
    // Persist alongside the event: filter changes make InstantSearch rebuild
    // the hits container, so cards re-mount AFTER the one-shot event fired and
    // checkedHandles blocks a re-publish. Re-mounted cards read this store.
    if (!window.__algoliaRangeDiscounts) window.__algoliaRangeDiscounts = new Map();
    window.__algoliaRangeDiscounts.set(String(info.handle), info);
    // Also persist (1h TTL) so a reload paints the discounted range on the first
    // render instead of recomputing it after the probe pass settles.
    plpSetRange(info.handle, info);
    document.dispatchEvent(new CustomEvent('algolia.discount.range', { detail: info }));
  }

  // The representative variant is probed in the main PROBE pass (it IS the hit's
  // variant_id), so its discount is already resolved in the shared store — read
  // it here instead of re-probing. Includes the OOS-sibling substitution the
  // availability pass may have published under the representative's id.
  function readRepDiscount(repId) {
    if (!repId) return null;
    return (window.__algoliaDiscounts && window.__algoliaDiscounts.get(String(repId)))
      || (SF.cacheGet && SF.cacheGet(String(repId)))
      || null;
  }

  // Build the discounted range for multi-variant cards WITHOUT fetching
  // /products/*.json (which 429s on discount-heavy pages).
  //
  // The old approach derived ONE discount ratio from the representative variant
  // and multiplied it onto both ends of the Algolia price range. That painted a
  // fake sale price on the end whose variant ISN'T actually on sale (only some
  // variants of a product may carry an automatic discount). Instead we resolve
  // each end independently: look up the product's variants (cached, shared with
  // the OOS-sibling pass), find the cheapest and most expensive, and probe the
  // real discount of each. An end keeps its REAL price unless that specific
  // variant is discounted.
  //
  // Caveat: only the two extreme variants are probed, so a deep discount on a
  // MIDDLE variant that would undercut the cheapest end isn't reflected — the
  // shown sale floor stays at or above the true floor (never below), so it
  // under-states rather than fabricates a discount. EXCEPTION: on the sale
  // collection (/collections/sale) the representative variant's discount is also
  // folded into the floor, closing the "Sale badge, no sale price" gap for
  // products whose discounted variant isn't a price extreme.
  // Range products per batch: each contributes 2 end-variant probe lines, so a
  // batch of 8 keeps the probe cart (and throttle bucket) light while letting
  // the first visible range cards paint before the rest of the grid resolves.
  const RANGE_BATCH_SIZE = 8;

  // Resolve and publish ONE product's discounted range from a settled end probe.
  function publishRangeEnd(e, probe) {
    const minInfo = probe.get(String(e.minVar.id));
    const maxInfo = probe.get(String(e.maxVar.id));
    // null = throttled/failed → leave the whole product unchecked so the next
    // render retries instead of publishing a half-known range.
    if (minInfo == null || maxInfo == null) return;

    checkedHandles.add(e.handle);

    // `unresolved` (an out-of-stock end whose qty-0 line proved nothing) is
    // treated as "no discount on that end" — its real price is shown.
    const minDisc = !minInfo.unresolved && minInfo.hasDiscount
      && minInfo.originalPrice > 0 && minInfo.finalPrice < minInfo.originalPrice;
    const maxDisc = !maxInfo.unresolved && maxInfo.hasDiscount
      && maxInfo.originalPrice > 0 && maxInfo.finalPrice < maxInfo.originalPrice;

    // Each end keeps its REAL price unless THAT variant is on sale.
    let discountMin = minDisc ? minInfo.finalPrice : e.minVar.price;
    let discountMax = maxDisc ? maxInfo.finalPrice : e.maxVar.price;

    // Sale page only: a discount can live on the representative (or any middle)
    // variant while the price extremes are full-price — that's the "Sale badge,
    // no sale price" gap. The representative's discounted price is real and
    // purchasable, so let it pull the sale floor down. Never fabricated:
    // Math.min only lowers the floor to a price that actually exists.
    const repInfo = SALE_PAGE ? readRepDiscount(e.repVariantId) : null;
    const repDisc = !!(repInfo && !repInfo.unresolved && repInfo.hasDiscount
      && repInfo.originalPrice > 0 && repInfo.finalPrice < repInfo.originalPrice);
    if (repDisc) discountMin = Math.min(discountMin, repInfo.finalPrice);

    if (!(discountMin < e.minVar.price) && !(discountMax < e.maxVar.price)) {
      // No end actually drops below its full price — card keeps its base
      // Algolia range. Persist a false marker (the renderer ignores it) so
      // this product is seeded into checkedHandles on the next load and we
      // don't re-run the variant lookup for it every reload.
      publishRangeDiscount({ handle: e.handle, hasRangeDiscount: false });
      return;
    }

    publishRangeDiscount({
      handle: e.handle,
      hasRangeDiscount: true,
      originalMin: e.minVar.price,
      originalMax: e.maxVar.price,
      discountMin,
      discountMax,
    });
  }

  async function processMultiVariantDiscounts(mainPassSettled) {
    const multiHits = extractMultiVariantHits()
      .filter(p => p.productId && !checkedHandles.has(p.handle));
    if (multiHits.length === 0) return;

    // One grid-wide variants lookup first (a 48-product grid = 1 request); the
    // per-batch calls below are then pure memo reads.
    await fetchProductVariants(multiHits.map(p => p.productId));

    // DOM-order batches run concurrently; each publishes the moment it settles.
    await Promise.all(chunk(multiHits, RANGE_BATCH_SIZE).map(async (batch) => {
      const variantsByProduct = await fetchProductVariants(batch.map(p => p.productId));

      // Resolve each product's range ends, collecting the end-variant ids into a
      // single batched/cached discount probe.
      const ends = [];
      const probeIds = new Set();
      for (const p of batch) {
        const variants = variantsByProduct.get(String(p.productId));
        if (!variants) continue; // lookup failed/throttled — retry next render
        const priced = variants.filter(v => v.price > 0).sort((a, b) => a.price - b.price);
        if (priced.length < 2) continue; // no real range to resolve

        const minVar = priced[0];
        const maxVar = priced[priced.length - 1];
        if (minVar.id === maxVar.id || !(maxVar.price > minVar.price)) continue;

        ends.push({ handle: p.handle, minVar, maxVar, repVariantId: p.repVariantId });
        probeIds.add(minVar.id);
        probeIds.add(maxVar.id);
      }
      if (ends.length === 0) return;

      // Range ends come from different products → resolve in isolation so one
      // product's high tier doesn't suppress another's (parallel peel).
      const probe = await SF.getDiscountsIsolated([...probeIds]);

      // Sale page only: publishRangeEnd folds the representative variant's
      // discount into the sale floor, and the rep is resolved by the main
      // pass — gate the publish (not the probing) on it.
      if (SALE_PAGE && mainPassSettled) await mainPassSettled;
      for (const e of ends) publishRangeEnd(e, probe);
    }));
  }

  // ---------------------------------------------------------------------------
  // Availability pre-pass
  // ---------------------------------------------------------------------------

  // Algolia's distinct picks ONE representative variant per product; when that
  // variant is out of stock the card shows "Notify Me" even though a sibling
  // sells fine on the PDP. Availability is resolved HERE, decoupled from the
  // discount probe, with the cheap products-nodes query — the Notify Me /
  // Add to Cart decision must not depend on cartCreate succeeding.
  const availabilityChecked = new Set(Object.keys(plpPersist));

  async function publishProductAvailability(variantIds) {
    const oosIds = [];
    for (const id of variantIds) {
      if (availabilityChecked.has(id)) continue;
      const meta = hitMeta.get(String(id));
      if (!meta || !meta.oos) continue;
      const cached = window.__algoliaDiscounts.get(String(id));
      if (cached && cached.productAvailable) { availabilityChecked.add(id); continue; }
      oosIds.push(id);
    }
    if (oosIds.length === 0) return;

    const subs = await resolveSubstitutes(oosIds);
    for (const id of oosIds) {
      if (!subs.has(id)) continue; // lookup failed/throttled — retry next render
      availabilityChecked.add(id);
      if (subs.get(id) == null) continue; // no purchasable variant — Notify Me is right
      const prev = window.__algoliaDiscounts.get(String(id));
      publishDiscount(id, Object.assign({ hasDiscount: false }, prev, { productAvailable: true }));
    }
  }

  // ---------------------------------------------------------------------------
  // Main processing
  // ---------------------------------------------------------------------------

  // Discounts resolve in small DOM-order batches so above-the-fold cards paint
  // first. Order-level discounts used to force a per-candidate VERIFY pass (they
  // allocate across every line of a multi-line cart) — the shared client now
  // strips them via the cart-level allocation guardrail, so probe amounts are
  // publishable directly. Only the OOS-sibling rescue needs a second probe.
  //
  // Batch size ≈ the first grid rows: sets the publish granularity (a slice
  // paints as a unit when it settles) and keeps each round-1 probe cart small
  // and cheap for the shared throttle bucket.
  const PROBE_BATCH_SIZE = 24;

  async function processNewHits() {
    let variantIds = extractVariantIdsFromHits();
    if (variantIds === null) variantIds = extractVariantIdsFromDOM();
    if (!variantIds || variantIds.length === 0) return;

    // Availability ("is this product actually purchasable?") resolves
    // CONCURRENTLY with the probes — it must not depend on cartCreate
    // succeeding, and the probes don't need it (publishDiscount's
    // never-regress guard makes either publish order safe).
    const availabilityPass = publishProductAvailability(variantIds);

    const newIds = variantIds.filter(id => !checkedVariants.has(id));

    if (newIds.length === 0) {
      await Promise.all([availabilityPass, processMultiVariantDiscounts(Promise.resolve())]);
      return;
    }

    // PROBE — getDiscountsIsolated resolves each product's OWN discount
    // (parallel peel; see storefront-client.js). DOM-order slices fire
    // concurrently and each publishes the moment it settles; the FIFO queue
    // keeps top-of-grid requests dispatching first. newIds is already in
    // hit/DOM order (extractVariantIds* preserve it).
    const oosReps = [];
    const sliceProbes = [];
    for (let i = 0; i < newIds.length; i += PROBE_BATCH_SIZE) {
      const slice = newIds.slice(i, i + PROBE_BATCH_SIZE);
      sliceProbes.push(SF.getDiscountsIsolated(slice).then(probe => {
        for (const id of slice) {
          const info = probe.get(String(id));
          // null = throttled/failed — stays UNCHECKED so the next render retries.
          if (info == null) continue;
          if (info.unresolved) {
            oosReps.push(id); // qty-0 line — discount unknowable via this variant
          } else {
            checkedVariants.add(id);
            publishDiscount(id, info); // already cached by the client
          }
        }
      }));
    }
    // Main pass = slice probes + OOS-sibling rescue, wrapped in one promise so
    // the range pass can run concurrently while the sale page gates its rep
    // fold-in on it.
    const mainPass = (async () => {
      await Promise.all(sliceProbes);

      // SIBLING phase — representative variant is out of stock, so its cart line
      // was clamped to qty 0 and proves nothing. Re-probe via an in-stock sibling
      // and publish whatever it says under the representative's id.
      if (oosReps.length > 0) {
        const subs = await resolveSubstitutes(oosReps);
        const subIds = [];
        const subToRep = new Map();
        for (const repId of oosReps) {
          if (!subs.has(repId)) continue; // lookup failed → retry next render
          const subId = subs.get(repId);
          if (subId == null) settle(repId, { hasDiscount: false }); // nothing purchasable
          else { subIds.push(subId); subToRep.set(subId, repId); }
        }
        if (subIds.length > 0) {
          // Siblings belong to different products → same per-cart suppression
          // applies, so resolve them in isolation too.
          const subProbe = await SF.getDiscountsIsolated(subIds);
          for (const subId of subIds) {
            const repId = subToRep.get(subId);
            const info = subProbe.get(String(subId));
            if (info == null) continue; // throttled/failed — retry next render
            // A purchasable sibling EXISTS — Algolia's representative variant is
            // just out of stock. Publish the sibling's discount info (or the
            // confirmed no-discount) under the representative's id, flagged so
            // cards stop showing "Notify Me" for products the PDP happily sells.
            // In-memory publish ONLY — never SF.cacheSet under the rep id: the
            // unified cache feeds the PDP and card sections, and they would
            // render the SIBLING's dollar amounts for the rep variant. The
            // sibling's own id is already cached by getDiscounts, so reloads
            // re-resolve cheaply.
            const base = info.unresolved ? { hasDiscount: false } : info;
            checkedVariants.add(repId);
            publishDiscount(repId, Object.assign({}, base, { productAvailable: true, substituted: true }));
          }
        }
      }
    })();

    await Promise.all([mainPass, processMultiVariantDiscounts(mainPass), availabilityPass]);
  }

  // ---------------------------------------------------------------------------
  // Event hook
  // ---------------------------------------------------------------------------

  let _processing = false;
  let _pendingRerun = false;
  let _retryTimer = null;
  const THROTTLE_RETRY_MS = 45 * 1000;

  document.addEventListener('algolia.instantSearch.onRender', () => {
    if (_processing) {
      _pendingRerun = true;
      return;
    }
    runProcessing();
  });

  // A throttled run leaves ids unchecked, and on an idle page there is no
  // next render to retry them — schedule ONE re-run after the cautious
  // window has passed.
  function scheduleThrottleRetry() {
    if (_retryTimer) return;
    _retryTimer = setTimeout(() => {
      _retryTimer = null;
      if (!_processing) runProcessing();
    }, THROTTLE_RETRY_MS);
  }

  async function runProcessing() {
    _processing = true;
    const throttledBefore = SF.stats ? SF.stats.throttled : 0;
    try {
      await processNewHits();
    } finally {
      _processing = false;

      // Signal that discount data is available for rendering
      if (!window.__discountsReady) {
        window.__discountsReady = true;
        document.dispatchEvent(new CustomEvent('algolia.discounts.ready'));
      }

      if (SF.stats && SF.stats.throttled > throttledBefore) {
        scheduleThrottleRetry();
      }

      if (_pendingRerun) {
        _pendingRerun = false;
        runProcessing();
      }
    }
  }
})();
